<template>
  <!--
    ArtifactDetail SECTION — DataAttribute artifact, "History" tab.

    WHAT: Every stored version of this DataAttribute, newest first. The DA SQL IS
      the metric definition, and a shared DA's blast radius is every artifact
      composing it — so "what did this say before someone changed it" is a
      recovery path, not a curiosity.

    CONTRACT (props { record, artifact, context }):
      READS  GET api/impact_analytics/attributes/{id}/history/
      EMITS  nothing — read-only.

    NOTES:
      - History is append-only from the day it was switched on (2026-08-16); it
        does not backfill, so an attribute untouched since then shows one entry
        or none. That is correct, not a bug.
      - history_user is only populated for edits made through the app; a
        management command or shell save records no user.
      - selfChrome: draws its own card.
  -->
  <div class="bg-white rounded-lg border border-gray-200">
    <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <div>
        <div class="text-xs font-semibold text-gray-700">Version history</div>
        <div class="text-[11px] text-gray-400 mt-0.5">
          Every save, newest first. Expand one to read its SQL as it stood then.
        </div>
      </div>
      <button
        class="text-[11px] text-gray-500 hover:text-gray-800 border border-gray-200 rounded px-2 py-1"
        :disabled="loading"
        @click="load"
      >
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </div>

    <div v-if="error" class="px-4 py-3 text-[11px] text-red-600 bg-red-50 border-b border-red-100">
      {{ error }}
    </div>

    <div v-if="!loading && !versions.length" class="px-4 py-8 text-center">
      <div class="text-xs text-gray-500">No history yet.</div>
      <div class="text-[11px] text-gray-400 mt-1 mx-auto">
        Versions are recorded from the next save onward — history does not
        reconstruct edits made before it was enabled.
      </div>
    </div>

    <div v-for="v in versions" :key="v.history_id" class="border-b border-gray-100 last:border-0">
      <div
        class="px-4 py-2.5 flex items-start gap-3 cursor-pointer hover:bg-gray-50"
        @click="toggle(v.history_id)"
      >
        <span
class="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-semibold flex-shrink-0"
              :class="typeClass(v.history_type)" :title="typeLabel(v.history_type)">
          {{ typeGlyph(v.history_type) }}
        </span>

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="text-xs text-gray-800">{{ formatDate(v.history_date) }}</span>
            <span
v-if="v.is_current"
                  class="text-[10px] px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
              current
            </span>
            <span class="text-[11px] text-gray-500">{{ v.user || 'system' }}</span>
          </div>
          <div class="mt-1 flex items-center gap-1 flex-wrap">
            <template v-if="v.changed_fields && v.changed_fields.length">
              <span
v-for="f in v.changed_fields" :key="f"
                    class="text-[10px] px-1.5 py-0.5 rounded border"
                    :class="f === 'sql_query'
                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : 'bg-gray-50 text-gray-600 border-gray-200'">
                {{ f }}
              </span>
            </template>
            <span v-else class="text-[10px] text-gray-400">
              {{ v.history_type === '+' ? 'created' : 'no tracked field changed' }}
            </span>
          </div>
        </div>

        <font-awesome-icon
          :icon="['fas', expanded[v.history_id] ? 'chevron-up' : 'chevron-down']"
          class="text-[10px] text-gray-400 mt-1 flex-shrink-0"
        />
      </div>

      <div v-if="expanded[v.history_id]" class="px-4 pb-3">
        <div class="grid grid-cols-3 gap-2 mb-2">
          <div v-for="f in ['name', 'role']" :key="f" class="text-[11px]">
            <span class="text-gray-400">{{ f }}:</span>
            <span class="text-gray-700 ml-1">{{ v[f] || '—' }}</span>
          </div>
          <div class="text-[11px]">
            <span class="text-gray-400">active:</span>
            <span class="text-gray-700 ml-1">{{ v.is_active ? 'yes' : 'no' }}</span>
          </div>
        </div>
        <pre class="text-[10px] font-mono bg-gray-50 border border-gray-200 rounded p-2 overflow-auto max-h-96 whitespace-pre-wrap">{{ v.sql_query || '—' }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'AttributeHistorySection',
  components: { FontAwesomeIcon },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  data() {
    return { versions: [], loading: false, error: '', expanded: {} };
  },
  watch: {
    'record.id': { immediate: true, handler() { this.load(); } },
  },
  methods: {
    async load() {
      if (!this.record?.id) return;
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.get(
          `api/impact_analytics/attributes/${this.record.id}/history/`);
        this.versions = Array.isArray(data) ? data : [];
      } catch (e) {
        this.error = `Could not load history: ${e?.response?.data?.error || e.message}`;
      } finally {
        this.loading = false;
      }
    },
    toggle(id) { this.expanded = { ...this.expanded, [id]: !this.expanded[id] }; },
    typeGlyph(t) { return t === '+' ? '+' : t === '-' ? '−' : '~'; },
    typeLabel(t) { return t === '+' ? 'created' : t === '-' ? 'deleted' : 'updated'; },
    typeClass(t) {
      if (t === '+') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      if (t === '-') return 'bg-red-50 text-red-700 border border-red-200';
      return 'bg-sky-50 text-sky-700 border border-sky-200';
    },
    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    },
  },
};
</script>
